import {
  BTN,
  MAX_PITCH,
  leanFromButtons,
  type InputCmd,
  type WeaponEquipSlot,
} from "@rietz/shared";
import { radPerCount, useSettings } from "../settings.js";
import { toggleFullscreen } from "./fullscreen.js";

/**
 * Mouse + keyboard capture. Look angles accumulate from raw pointer-lock
 * deltas and are read every rendered frame by the camera; `sampleCmd`
 * snapshots them once per tick. Yaw 0 looks down −Z and increasing yaw turns
 * left, hence the subtraction on `movementX`. Sensitivity comes from
 * `settings.ts` on the CS2 scale, cached in a field and refreshed by
 * subscription — a thousand `mousemove`s a second may not touch a store.
 */

/** Stop just short of straight up/down so the view basis never degenerates. */
const PITCH_LIMIT = MAX_PITCH - 0.01;

const TWO_PI = Math.PI * 2;

/**
 * Trigger pulls buffered between two `consumeFirePresses`: two, since a fast
 * finger can land two clicks in one frame; more is a macro.
 */
const MAX_BUFFERED_FIRE = 2;

/**
 * Buffered presses of everything else — one is enough for deliberate
 * one-at-a-time actions.
 */
const MAX_BUFFERED_ACTION = 1;

export interface InputOptions {
  canvas: HTMLCanvasElement;
  /** Loading overlay. It is never used as a click-to-enter gate. */
  scrim: HTMLElement | null;
}

export class Input {
  yaw = 0;
  pitch = 0;

  /** True while the pointer is locked to the canvas. */
  locked = false;

  /**
   * Debug fly mode (F6), sampled into every command — noclip is an input bit
   * `stepMovement` reads on both ends.
   */
  noclip = false;

  /**
   * The mounted lamp or laser is on — a latch like `noclip`: T flips it and it
   * rides every command until T flips it back. Survives everything that empties
   * `down` (`clearHeld` leaves it alone). Public: `sampleCmd` puts it on the
   * wire and the 1P effect reads it every frame.
   */
  tacticalOn = false;

  private readonly canvas: HTMLCanvasElement;
  private readonly scrim: HTMLElement | null;

  private readonly down = new Set<string>();
  private mouseButtons = 0;

  /**
   * Left-mouse presses since the last `consumeFirePresses`. Firing is
   * edge-triggered and its own message; buffering stops a click between samples
   * being swallowed.
   */
  private firePresses = 0;

  /**
   * `F` presses since the last `consumeInteractPresses` — the EDGE, so a held F
   * opens one container. The held bit still rides in `InputCmd`.
   */
  private interactPresses = 0;

  /**
   * `V` presses since the last `consumeMagCheckPresses` — an edge, its own
   * `magCheck` message. V because the genre's chord on T is a bad browser
   * binding (Alt steals focus), V is free, and it sits under the hand on R.
   */
  private magCheckPresses = 0;

  /**
   * The two edges of the grenade key, buffered separately: pressing pulls the
   * pin, releasing throws. Counted, so a tap inside one frame is a real throw.
   * `grenadeDown` pairs them — an auto-repeat cannot pull a second pin, and a
   * keyup with no press behind it cannot throw.
   */
  private grenadePresses = 0;
  private grenadeReleases = 0;
  private grenadeDown = false;

  /** `Tab` presses: requests to toggle the inventory screen. */
  private inventoryToggles = 0;

  /** `Escape` presses: requests to close whatever screen is up. */
  private menuCloses = 0;

  /** Most recent dedicated weapon hotkey pressed since the last frame. */
  private weaponSelection: WeaponEquipSlot | null = null;

  /** `C` toggles crouch. No Ctrl binding: Ctrl+W closes the tab. */
  private crouchToggled = false;

  /**
   * While false, clicking does not grab the pointer and the scrim stays hidden
   * — the death overlay's button needs the cursor.
   */
  private lockAllowed = true;

  /**
   * While false, clicking does not grab the pointer and the loading overlay
   * stays UP — the opposite of `lockAllowed`, which hides the scrim. Waiting for
   * a map, the loading display is the only thing worth looking at.
   */
  private mapReady = true;

  private disposed = false;

  /**
   * Radians of yaw per mouse count, cached (`onMouseMove` runs at 1000 Hz); the
   * subscription below keeps it current.
   */
  private lookRadPerCount = radPerCount(useSettings.getState().sensitivity);

  /** Drops the sensitivity subscription. Called from `dispose`. */
  private readonly unsubscribeSettings: () => void;

  constructor(opts: InputOptions) {
    this.canvas = opts.canvas;
    this.scrim = opts.scrim;

    this.unsubscribeSettings = useSettings.subscribe((s) => {
      this.lookRadPerCount = radPerCount(s.sensitivity);
    });

    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mousedown", this.onMouseDown);
    document.addEventListener("mouseup", this.onMouseUp);
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    // Right mouse is ADS; the context menu would eat it.
    this.canvas.addEventListener("contextmenu", this.onContextMenu);

    this.updateScrim();
  }

  /** Snapshots the current intent as a wire command. */
  sampleCmd(seq: number): InputCmd {
    let buttons = 0;
    if (this.down.has("KeyW") || this.down.has("ArrowUp")) buttons |= BTN.FWD;
    if (this.down.has("KeyS") || this.down.has("ArrowDown")) buttons |= BTN.BACK;
    if (this.down.has("KeyA") || this.down.has("ArrowLeft")) buttons |= BTN.LEFT;
    if (this.down.has("KeyD") || this.down.has("ArrowRight")) {
      buttons |= BTN.RIGHT;
    }
    if (this.down.has("ShiftLeft") || this.down.has("ShiftRight")) {
      buttons |= BTN.SPRINT;
    }
    if (this.crouchToggled) buttons |= BTN.CROUCH;
    if (this.down.has("Space")) buttons |= BTN.JUMP;
    if (this.down.has("KeyR")) buttons |= BTN.RELOAD;
    // F only: E is the right lean now, and a key cannot be both.
    if (this.down.has("KeyF")) buttons |= BTN.INTERACT;
    if (this.down.has("KeyQ")) buttons |= BTN.LEAN_L;
    if (this.down.has("KeyE")) buttons |= BTN.LEAN_R;
    if (this.adsHeld) buttons |= BTN.ADS;
    if (this.noclip) buttons |= BTN.NOCLIP;
    // Held for as long as the device is on; the bit going quiet is what tells
    // everyone else the light went out.
    if (this.tacticalOn) buttons |= BTN.TACTICAL;

    return { seq, buttons, yaw: this.yaw, pitch: this.pitch };
  }

  /**
   * The peek asked for now: −1 for Q, +1 for E, 0 for neither or both. Read by
   * the camera every frame; `sampleCmd` sends the same bits — both through the
   * shared `leanFromButtons`, so the view and the hitboxes cannot disagree.
   */
  get lean(): -1 | 0 | 1 {
    let bits = 0;
    if (this.down.has("KeyQ")) bits |= BTN.LEAN_L;
    if (this.down.has("KeyE")) bits |= BTN.LEAN_R;
    return leanFromButtons(bits);
  }

  /** True while the ADS (right mouse) button is held. */
  get adsHeld(): boolean {
    return (this.mouseButtons & 2) !== 0;
  }

  /**
   * True while the trigger is HELD — what full auto fires from. Semi stays on
   * `consumeFirePresses`.
   */
  get fireHeld(): boolean {
    return (this.mouseButtons & 1) !== 0;
  }

  /** Takes and clears the buffered trigger pulls. Once per rendered frame. */
  consumeFirePresses(): number {
    const n = this.firePresses;
    this.firePresses = 0;
    return n;
  }

  /** Takes and clears the buffered `E` presses. Same contract as the trigger. */
  consumeInteractPresses(): number {
    const n = this.interactPresses;
    this.interactPresses = 0;
    return n;
  }

  /** Takes and clears the buffered `V` presses — magazine checks asked for.
   *  Same contract as the trigger: whoever calls it owns them. */
  consumeMagCheckPresses(): number {
    const n = this.magCheckPresses;
    this.magCheckPresses = 0;
    return n;
  }

  /** Takes and clears buffered G presses — pins pulled. Same contract as the
   *  trigger: whoever calls it owns them. */
  consumeGrenadePresses(): number {
    const n = this.grenadePresses;
    this.grenadePresses = 0;
    return n;
  }

  /** Takes and clears buffered G releases — grenades let go of. */
  consumeGrenadeReleases(): number {
    const n = this.grenadeReleases;
    this.grenadeReleases = 0;
    return n;
  }

  /** Takes and clears buffered `Tab` presses — inventory screen toggles. */
  consumeInventoryToggles(): number {
    const n = this.inventoryToggles;
    this.inventoryToggles = 0;
    return n;
  }

  /** Takes and clears buffered `Escape` presses — "close whatever is up". */
  consumeMenuCloses(): number {
    const n = this.menuCloses;
    this.menuCloses = 0;
    return n;
  }

  consumeWeaponSelection(): WeaponEquipSlot | null {
    const slot = this.weaponSelection;
    this.weaponSelection = null;
    return slot;
  }

  /**
   * Grabs the pointer with `unadjustedMovement: true` — no OS acceleration, so
   * `movementX` is 1:1 with mouse counts and the CS2 sensitivity scale is
   * physically true. Browsers without it reject, throw, or return void; all
   * three end in one retry of the plain request.
   */
  requestLock(): void {
    if (this.locked || !this.lockAllowed || !this.mapReady) return;
    let pending: unknown;
    try {
      pending = this.canvas.requestPointerLock({ unadjustedMovement: true });
    } catch {
      this.requestLockPlain();
      return;
    }
    if (pending instanceof Promise) {
      pending.catch(() => {
        this.requestLockPlain();
      });
    }
  }

  /**
   * The fallback: pointer lock with no options. Its refusal is swallowed —
   * usually the cooldown after an Escape exit; the next click retries.
   */
  private requestLockPlain(): void {
    if (this.locked || !this.lockAllowed || !this.mapReady) return;
    try {
      // Newer DOM lib types return a promise here, older ones `void`.
      const pending: unknown = this.canvas.requestPointerLock();
      if (pending instanceof Promise) pending.catch(() => undefined);
    } catch {
      // A browser that refuses outright. There is nothing left to try.
    }
  }

  /** Hands the cursor back — see `lockAllowed`. */
  releaseLock(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }

  /**
   * Enables/disables pointer capture. Disabling releases it and buries buffered
   * trigger pulls, so a click on the death overlay is not a shot on respawn.
   */
  setLockAllowed(allowed: boolean): void {
    if (this.lockAllowed === allowed) return;
    this.lockAllowed = allowed;
    if (!allowed) {
      this.firePresses = 0;
      this.interactPresses = 0;
      this.releaseLock();
    }
    this.updateScrim();
  }

  /**
   * Whether there is a world to be let into (`mapReady`). The loading overlay
   * is tied to this, not to pointer lock.
   */
  setMapReady(ready: boolean): void {
    if (this.mapReady === ready) return;
    this.mapReady = ready;
    if (!ready) this.releaseLock();
    this.updateScrim();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribeSettings();
    document.removeEventListener("pointerlockchange", this.onPointerLockChange);
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mousedown", this.onMouseDown);
    document.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    this.canvas.removeEventListener("contextmenu", this.onContextMenu);
  }

  // ---------------------------------------------------------------------

  private readonly onMouseDown = (ev: MouseEvent): void => {
    if (!this.locked) {
      // The click that grabs the pointer must not also pull the trigger.
      this.requestLock();
      return;
    }
    this.mouseButtons = ev.buttons;
    if (ev.button === 0 && this.firePresses < MAX_BUFFERED_FIRE) {
      this.firePresses += 1;
    }
  };

  private readonly onContextMenu = (ev: Event): void => {
    ev.preventDefault();
  };

  private readonly onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    if (!this.locked) {
      // Releasing the mouse must not leave the player sprinting into a wall.
      this.clearHeld();
    }
    this.updateScrim();
  };

  private readonly onMouseMove = (ev: MouseEvent): void => {
    if (!this.locked) return;
    this.yaw -= ev.movementX * this.lookRadPerCount;
    this.pitch -= ev.movementY * this.lookRadPerCount;

    if (this.pitch > PITCH_LIMIT) this.pitch = PITCH_LIMIT;
    else if (this.pitch < -PITCH_LIMIT) this.pitch = -PITCH_LIMIT;

    // Keep yaw in (−π, π]. `stepMovement` only ever takes its sin/cos so this
    // is behaviour-neutral, and it keeps the wire value small forever.
    if (this.yaw > Math.PI || this.yaw < -Math.PI) {
      this.yaw -= TWO_PI * Math.floor((this.yaw + Math.PI) / TWO_PI);
    }
  };

  private readonly onMouseUp = (ev: MouseEvent): void => {
    this.mouseButtons = ev.buttons;
  };

  private readonly onKeyDown = (ev: KeyboardEvent): void => {
    // --- Screen keys, whether or not the pointer is captured: the inventory
    // releases the pointer, so the keys that close it arrive unlocked.
    if (ev.code === "Tab") {
      // Without this the browser moves focus out of the canvas and the next
      // key press lands in the URL bar.
      ev.preventDefault();
      if (!ev.repeat && this.inventoryToggles < MAX_BUFFERED_ACTION) {
        this.inventoryToggles += 1;
      }
      return;
    }
    if (ev.code === "Escape") {
      if (!ev.repeat && this.menuCloses < MAX_BUFFERED_ACTION) this.menuCloses += 1;
      return;
    }

    // F10 is fullscreen (`fullscreen.ts`), taken locked or not.
    if (ev.code === "F10") {
      ev.preventDefault();
      if (!ev.repeat) toggleFullscreen();
      return;
    }

    if (!this.locked) return;
    if (!ev.repeat) {
      if (ev.code === "Digit1") this.weaponSelection = "pistol";
      else if (ev.code === "Digit2") this.weaponSelection = "weapon";
      else if (ev.code === "Digit3") this.weaponSelection = "weapon2";
    }
    if (ev.code === "KeyC" && !ev.repeat) {
      this.crouchToggled = !this.crouchToggled;
    }
    // T is a switch, not a trigger: `!ev.repeat` so a finger left on the key
    // does not flick the torch on and off thirty times a second.
    if (ev.code === "KeyT" && !ev.repeat) {
      this.tacticalOn = !this.tacticalOn;
    }
    if (ev.code === "KeyF" && !ev.repeat && this.interactPresses < MAX_BUFFERED_ACTION) {
      this.interactPresses += 1;
    }
    // V: the magazine check. Same shape as F — one edge, one buffered slot.
    if (ev.code === "KeyV" && !ev.repeat && this.magCheckPresses < MAX_BUFFERED_ACTION) {
      this.magCheckPresses += 1;
    }
    // G: the pin. `grenadeDown` rather than `!ev.repeat` alone, because the two
    // edges have to stay matched — see the fields.
    if (ev.code === "KeyG" && !this.grenadeDown) {
      this.grenadeDown = true;
      if (this.grenadePresses < MAX_BUFFERED_ACTION) this.grenadePresses += 1;
    }
    this.down.add(ev.code);
    if (ev.code === "Space" || ev.code.startsWith("Arrow")) ev.preventDefault();
  };

  private readonly onKeyUp = (ev: KeyboardEvent): void => {
    this.down.delete(ev.code);
    // Not gated on `locked`: losing the pointer with a pin out must still
    // throw — the fuse is running either way.
    if (ev.code === "KeyG") this.releaseGrenade();
  };

  private readonly onBlur = (): void => {
    this.clearHeld();
  };

  private clearHeld(): void {
    this.down.clear();
    this.mouseButtons = 0;
    // Unconsumed trigger pulls and reaches are stale intent. The SCREEN keys
    // are
    // not cleared — releasing the pointer is what opening the inventory does.
    this.firePresses = 0;
    this.interactPresses = 0;
    // ...and for a glance into a magazine, which is a thing you ask for about
    // the situation you are in and not about the one you come back to.
    this.magCheckPresses = 0;
    // A grenade is not buried: the fuse burns on the server whatever this
    // window does, so losing focus THROWS.
    this.releaseGrenade();
  }

  /** Turns a held G into a release, once. Shared by the key-up and the blur. */
  private releaseGrenade(): void {
    if (!this.grenadeDown) return;
    this.grenadeDown = false;
    if (this.grenadeReleases < MAX_BUFFERED_ACTION) this.grenadeReleases += 1;
  }

  private updateScrim(): void {
    if (this.scrim === null) return;
    this.scrim.hidden = this.mapReady || this.locked || !this.lockAllowed;
  }
}
